import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { StopCause } from "../../Types/StopCause";
import {
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHeader,
    TableHeaderCell,
    Button,
} from "@fluentui/react-components";
import {
    ChevronLeft20Regular,
    ChevronRight20Regular,
    ArrowUp20Regular,
    ArrowDown20Regular,
    Document20Regular,
    Document20Filled,
} from "@fluentui/react-icons";
import Modal from "../Modal";
import { useAppDispatch } from "../../app/hooks";
import { openFileGallery } from "../../Slices/FileGallerySlice";
import { getFilenameFromContentDisposition } from "../../utils/httpFileMeta";

type SortField = 'machine' | 'alias' | 'catalogue code' | 'reason' | 'mwc' | 'startTime' | 'endTime' | 'duration' | 'comment' | 'file';
type SortDirection = 'asc' | 'desc' | null;

interface StopCodingListProps {
    stopCauses: StopCause[];
    cascadeFilteredStopCauses?: StopCause[];
    selectedReason?: string;
    title?: string;
    headerContent?: React.ReactNode;
}

const StopCodingList: React.FC<StopCodingListProps> = ({ stopCauses, cascadeFilteredStopCauses, selectedReason, title, headerContent }) => {
    const dispatch = useAppDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);
    const [selectedStop, setSelectedStop] = useState<StopCause | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [attachmentNameById, setAttachmentNameById] = useState<Record<string, string>>({});

    const desktopScrollRef = useRef<HTMLDivElement | null>(null);
    const desktopScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const desktopScrollbarThumbRef = useRef<HTMLDivElement | null>(null);
    const desktopDragStateRef = useRef<{ startX: number; startScrollLeft: number } | null>(null);

    const [desktopCanScroll, setDesktopCanScroll] = useState(false);
    const [desktopThumbWidth, setDesktopThumbWidth] = useState(0);
    const [desktopThumbLeft, setDesktopThumbLeft] = useState(0);
    const [isDesktopThumbDragging, setIsDesktopThumbDragging] = useState(false);
    const itemsPerPage = 20;

    const baseData = cascadeFilteredStopCauses || stopCauses;

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortDirection(null);
                setSortField(null);
            }
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(1);
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return null;
        if (sortDirection === 'asc') return <ArrowUp20Regular />;
        if (sortDirection === 'desc') return <ArrowDown20Regular />;
        return null;
    };

    const formatDate = (value: Date | string | number | null | undefined) => {
        if (!value) return 'N/A';

        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return 'N/A';

        return new Intl.DateTimeFormat('sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(date);
    };

    const formatDuration = (duration: any) => {
        if (!duration) return 'N/A';

        const { days = 0, hours = 0, minutes = 0, seconds = 0 } = duration;

        const parts = [
            days > 0 ? `${days}d` : null,
            hours > 0 ? `${hours}h` : null,
            minutes > 0 ? `${minutes}m` : null,
            seconds > 0 ? `${seconds}s` : null
        ].filter(Boolean);
        if (parts.length === 0) return '0s';

        return parts.join(' ');
    };

    const getReasonLabel = (reason?: string | null) => {
        if (!reason) return 'Unknown';
        return reason.includes('PI Point not found') ? 'Oklassificerat' : (reason.trim() || 'Unknown');
    };

    const displayData = useMemo(() => {
        if (!sortField || !sortDirection) return baseData;

        return [...baseData].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            const compareStrings = (left: string, right: string) => {
                return sortDirection === 'asc'
                    ? left.localeCompare(right)
                    : right.localeCompare(left);
            };

            switch (sortField) {
                case 'machine':
                    aValue = a.MachineId || '';
                    bValue = b.MachineId || '';
                    break;
                case 'alias':
                    aValue = a.Alias || '';
                    bValue = b.Alias || '';
                    break;
                case 'reason':
                    aValue = getReasonLabel(a.MachineStopReason);
                    bValue = getReasonLabel(b.MachineStopReason);
                    break;
                case 'catalogue code':
                    aValue = a.CatalogueCode || '';
                    bValue = b.CatalogueCode || '';
                    break;
                case 'mwc':
                    aValue = a.MWC || '';
                    bValue = b.MWC || '';
                    break;
                case 'startTime':
                    aValue = new Date(a.StartTime).getTime();
                    bValue = new Date(b.StartTime).getTime();
                    break;
                case 'endTime':
                    aValue = new Date(a.EndTime).getTime();
                    bValue = new Date(b.EndTime).getTime();
                    break;
                case 'duration':
                    aValue = a.Duration.totalMilliseconds
                    bValue = b.Duration.totalMilliseconds;
                    break;
                case 'comment':
                    aValue = a.Comment || '';
                    bValue = b.Comment || '';
                    break;
                case 'file':
                    aValue = (a.AttachedFileIds?.length ?? 0) || (a.HasImage ? 1 : 0);
                    bValue = (b.AttachedFileIds?.length ?? 0) || (b.HasImage ? 1 : 0);
                    break;
                default:
                    return 0;
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                const primary = compareStrings(aValue, bValue);
                if (primary !== 0) return primary;

                if (sortField === 'machine') {
                    return compareStrings(a.Alias || '', b.Alias || '');
                }

                return 0;
            }

            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        });
    }, [baseData, sortField, sortDirection]);

    useEffect(() => {
        setCurrentPage(1);
    }, [stopCauses, cascadeFilteredStopCauses]);

    const totalPages = Math.ceil(displayData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = displayData.slice(startIndex, endIndex);

    const handlePreviousPage = () => {
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    const handleRowClick = (stop: StopCause) => {
        setSelectedStop(stop);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedStop(null);
    };

    useEffect(() => {
        if (!isModalOpen) return;
        const ids = (selectedStop?.AttachedFileIds || []).filter(Boolean);
        if (ids.length === 0) return;

        const controller = new AbortController();

        const loadNames = async () => {
            await Promise.all(
                ids.map(async (fileId) => {
                    if (!fileId) return;
                    if (attachmentNameById[fileId]) return;

                    try {
                        const res = await fetch(`${process.env.REACT_APP_API_URL}/files/download/${fileId}`, {
                            signal: controller.signal,
                        });
                        if (!res.ok) throw new Error(String(res.status));

                        const name =
                            getFilenameFromContentDisposition(res.headers.get("content-disposition")) ?? fileId;
                        setAttachmentNameById((prev) => (prev[fileId] ? prev : { ...prev, [fileId]: name }));

                        try {
                            res.body?.cancel();
                        } catch {
                            // ignore
                        }
                    } catch (e) {
                        if (e instanceof Error && e.name === "AbortError") return;
                        setAttachmentNameById((prev) => (prev[fileId] ? prev : { ...prev, [fileId]: fileId }));
                    }
                })
            );
        };

        loadNames();
        return () => controller.abort();
        // Intentionally exclude attachmentNameById to avoid re-fetch loops.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, selectedStop?.Id]);

    const updateDesktopScrollbar = useCallback(() => {
        const scrollEl = desktopScrollRef.current;
        const trackEl = desktopScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;

        const scrollWidth = scrollEl.scrollWidth;
        const clientWidth = scrollEl.clientWidth;
        const scrollLeft = scrollEl.scrollLeft;
        const trackWidth = trackEl.clientWidth;

        const canScroll = scrollWidth > clientWidth + 1;
        setDesktopCanScroll(canScroll);

        if (!canScroll || trackWidth <= 0) {
            setDesktopThumbWidth(0);
            setDesktopThumbLeft(0);
            return;
        }

        const minThumbWidth = 24;
        const thumbWidth = Math.max(minThumbWidth, (clientWidth / scrollWidth) * trackWidth);
        const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
        const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

        const thumbLeft = maxScrollLeft === 0 ? 0 : (scrollLeft / maxScrollLeft) * maxThumbLeft;
        setDesktopThumbWidth(thumbWidth);
        setDesktopThumbLeft(thumbLeft);
    }, []);

    useLayoutEffect(() => {
        updateDesktopScrollbar();
    }, [updateDesktopScrollbar, title, paginatedData.length]);

    useEffect(() => {
        const scrollEl = desktopScrollRef.current;
        if (!scrollEl) return;

        const onScroll = () => updateDesktopScrollbar();
        scrollEl.addEventListener('scroll', onScroll, { passive: true });

        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => updateDesktopScrollbar());
            ro.observe(scrollEl);
            if (desktopScrollbarTrackRef.current) ro.observe(desktopScrollbarTrackRef.current);
        } else {
            window.addEventListener('resize', updateDesktopScrollbar);
        }

        return () => {
            scrollEl.removeEventListener('scroll', onScroll);
            if (ro) ro.disconnect();
            else window.removeEventListener('resize', updateDesktopScrollbar);
        };
    }, [updateDesktopScrollbar]);

    useEffect(() => {
        if (!isDesktopThumbDragging) return;

        const onPointerMove = (e: PointerEvent) => {
            const scrollEl = desktopScrollRef.current;
            const trackEl = desktopScrollbarTrackRef.current;
            const drag = desktopDragStateRef.current;
            if (!scrollEl || !trackEl || !drag) return;

            const scrollWidth = scrollEl.scrollWidth;
            const clientWidth = scrollEl.clientWidth;
            const trackWidth = trackEl.clientWidth;
            const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
            if (maxScrollLeft === 0 || trackWidth <= 0) return;

            const maxThumbLeft = Math.max(0, trackWidth - desktopThumbWidth);
            if (maxThumbLeft === 0) return;

            const deltaX = e.clientX - drag.startX;
            const scrollDelta = (deltaX / maxThumbLeft) * maxScrollLeft;
            scrollEl.scrollLeft = drag.startScrollLeft + scrollDelta;
        };

        const stopDragging = () => {
            setIsDesktopThumbDragging(false);
            desktopDragStateRef.current = null;
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', stopDragging);
        window.addEventListener('pointercancel', stopDragging);

        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', stopDragging);
            window.removeEventListener('pointercancel', stopDragging);
        };
    }, [isDesktopThumbDragging, desktopThumbWidth]);

    const handleDesktopThumbPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        const scrollEl = desktopScrollRef.current;
        if (!scrollEl) return;

        desktopDragStateRef.current = { startX: e.clientX, startScrollLeft: scrollEl.scrollLeft };
        setIsDesktopThumbDragging(true);
        desktopScrollbarThumbRef.current?.setPointerCapture?.(e.pointerId);
        e.preventDefault();
    };

    return (
        <div className="w-full -mt-2">

            <div className="hidden md:block border-8 border-white dark:border-primary-dark">
                {headerContent ? headerContent : title ? <h2 className="p-2 text-lg font-semibold">{title}</h2> : null}
                <div ref={desktopScrollRef} className="overflow-x-auto w-full min-w-0 px-4">
                    <Table aria-label="Stop coding list" className="w-full min-w-max">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell className="w-24">
                                    <button
                                        onClick={() => handleSort('machine')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        Machine {getSortIcon('machine')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="w-20">
                                    <button
                                        onClick={() => handleSort('alias')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        Alias {getSortIcon('alias')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="w-48">
                                    <button
                                        onClick={() => handleSort('catalogue code')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        Catalogue Code {getSortIcon('catalogue code')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="w-96">
                                    <button
                                        onClick={() => handleSort('reason')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        Stop Reason {getSortIcon('reason')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="w-20">
                                    <button
                                        onClick={() => handleSort('mwc')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        MWC {getSortIcon('mwc')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="w-40">
                                    <button
                                        onClick={() => handleSort('startTime')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        Start Time {getSortIcon('startTime')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="w-40">
                                    <button
                                        onClick={() => handleSort('endTime')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        End Time {getSortIcon('endTime')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="w-32">
                                    <button
                                        onClick={() => handleSort('duration')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        Duration {getSortIcon('duration')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="">
                                    <button
                                        onClick={() => handleSort('comment')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        Comment {getSortIcon('comment')}
                                    </button>
                                </TableHeaderCell>
                                <TableHeaderCell className="w-16">
                                    <button
                                        onClick={() => handleSort('file')}
                                        className="flex items-center gap-1 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 underline"
                                    >
                                        File {getSortIcon('file')}
                                    </button>
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {displayData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} className="text-center p-4">
                                        No stop causes found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedData.map((stop, index) => (
                                    <TableRow
                                        key={stop.Id || index}
                                        onClick={() => handleRowClick(stop)}
                                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                    >
                                        <TableCell>{stop.MachineId}</TableCell>
                                        <TableCell className="max-w-[160px] truncate" title={stop.Alias}>
                                            {stop.Alias || '-'}
                                        </TableCell>
                                        <TableCell className="max-w-[120px] truncate" title={stop.CatalogueCode}>
                                            {stop.CatalogueCode || '-'}
                                        </TableCell>
                                        <TableCell className="max-w-[200px] truncate" title={stop.MachineStopReason}>
                                            {getReasonLabel(stop.MachineStopReason)}
                                        </TableCell>
                                        <TableCell>{stop.MWC}</TableCell>
                                        <TableCell className="max-w-[200px] truncate">{formatDate(stop.StartTime)}</TableCell>
                                        <TableCell className="max-w-[200px] truncate">{formatDate(stop.EndTime)}</TableCell>
                                        <TableCell>{formatDuration(stop.Duration)}</TableCell>
                                        <TableCell className="max-w-[200px] truncate" title={stop.Comment}>
                                            {stop.Comment || '-'}
                                        </TableCell>
                                        <TableCell>
                                            {stop.HasImage ? (
                                                <Document20Filled className="text-blue-600 dark:text-blue-400" />
                                            ) : (
                                                <Document20Regular className="text-gray-400" />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
                {


                    <div className={`mt-2 px-2 pb-2 ${desktopCanScroll ? '' : ' pointer-events-none'}`}>
                        <div
                            ref={desktopScrollbarTrackRef}
                            className=" relative h-4 rounded select-none "
                            aria-hidden={!desktopCanScroll}
                        >
                            <div
                                ref={desktopScrollbarThumbRef}
                                className={`bg-primary-dark absolute top-0 h-3  mt-0.5 rounded ${isDesktopThumbDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                                style={{ width: `${desktopThumbWidth}px`, transform: `translateX(${desktopThumbLeft}px)` }}
                                onPointerDown={handleDesktopThumbPointerDown}
                            />
                        </div>
                    </div>
                }
            </div>

            {/* Mobile Card View - visible on small screens */}
            <div className="md:hidden max-w-full overflow-hidden border-8 border-white dark:border-primary-dark divide-y-8 divide-white dark:divide-primary-dark">
                {headerContent ? headerContent : title ? <h2 className="p-2 text-lg font-semibold">{title}</h2> : null}
                {displayData.length === 0 ? (
                    <div className="text-center p-8 rounded-lg">
                        No stop causes found
                    </div>
                ) : (
                    paginatedData.map((stop, index) => (
                        <div
                            key={stop.Id || index}
                            onClick={() => handleRowClick(stop)}
                            className="p-4 max-w-full cursor-pointer transition-colors"
                        >
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">Machine</div>
                                    <div className="text-gray-900 dark:text-gray-100">{stop.MachineId}</div>
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">Alias</div>
                                    <div className="text-gray-900 dark:text-gray-100 truncate" title={stop.Alias}>
                                        {stop.Alias || '-'}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">Catalogue Code</div>
                                    <div className="text-gray-900 dark:text-gray-100 truncate" title={stop.CatalogueCode}>
                                        {stop.CatalogueCode || '-'}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">Duration</div>
                                    <div className="text-gray-900 dark:text-gray-100">{formatDuration(stop.Duration)}</div>
                                </div>
                                <div className="col-span-2">
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">Stop Reason</div>
                                    <div className="text-gray-900 dark:text-gray-100">
                                        {getReasonLabel(stop.MachineStopReason)}
                                    </div>
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">MWC</div>
                                    <div className="text-gray-900 dark:text-gray-100">{stop.MWC}</div>
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">Start Time</div>
                                    <div className="text-gray-900 dark:text-gray-100 text-xs">{formatDate(stop.StartTime)}</div>
                                </div>
                                <div>
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">End Time</div>
                                    <div className="text-gray-900 dark:text-gray-100 text-xs">{formatDate(stop.EndTime)}</div>
                                </div>
                                {stop.Comment && stop.Comment !== '-' && (
                                    <div className="col-span-2">
                                        <div className="font-semibold text-gray-500 dark:text-gray-400">Comment</div>
                                        <div className="text-gray-900 dark:text-gray-100">{stop.Comment}</div>
                                    </div>
                                )}
                                <div>
                                    <div className="font-semibold text-gray-500 dark:text-gray-400">File</div>
                                    <div className="text-gray-900 dark:text-gray-100">
                                        {stop.HasImage ? (
                                            <Document20Filled className="text-blue-600 dark:text-blue-400" />
                                        ) : (
                                            <Document20Regular className="text-gray-400" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {displayData.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {startIndex + 1}-{Math.min(endIndex, displayData.length)} of {displayData.length} matching stop cause{displayData.length !== 1 ? 's' : ''}
                        {selectedReason && ` for "${selectedReason}"`}
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center gap-2">
                            <Button
                                appearance="subtle"
                                icon={<ChevronLeft20Regular />}
                                onClick={handlePreviousPage}
                                disabled={currentPage === 1}
                            />
                            <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[80px] text-center">
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                appearance="subtle"
                                icon={<ChevronRight20Regular />}
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Detail Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title="Stop Cause Details"
                width="96"
            >
                {selectedStop && (
                    <div className="space-y-4 max-w-full overflow-hidden">
                        {selectedStop.AttachedFileIds && selectedStop.AttachedFileIds.length > 0 && (
                            <div className="w-full rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm mb-2">Attachments</div>

                                <div className="max-h-48 overflow-y-auto space-y-1 pr-1">
                                    {selectedStop.AttachedFileIds.filter(Boolean).map((fileId, idx) => {
                                        const galleryFiles = selectedStop.AttachedFileIds.filter(Boolean).map((id) => ({ uuid: id }));
                                        const label = attachmentNameById[fileId] ?? fileId;

                                        return (
                                            <button
                                                key={`${fileId}-${idx}`}
                                                type="button"
                                                className="w-full flex items-center gap-2 p-2 rounded text-left hover:bg-gray-50 dark:hover:bg-gray-800 bg-transparent border-none cursor-pointer"
                                                onClick={() =>
                                                    dispatch(
                                                        openFileGallery({
                                                            files: galleryFiles,
                                                            initialIndex: idx,
                                                            title: "Attachments",
                                                        })
                                                    )
                                                }
                                                title={label}
                                            >
                                                <Document20Regular className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
                                                <span className="text-sm text-primary-dark dark:text-primary-light truncate">{label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-full">
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Machine ID</div>
                                <div className="text-lg truncate">{selectedStop.MachineId}</div>
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">MWC</div>
                                <div className="text-lg truncate">{selectedStop.MWC}</div>
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Alias</div>
                                <div className="text-lg truncate">{selectedStop.Alias}</div>
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Catalogue Code</div>
                                <div className="text-lg">{selectedStop.CatalogueCode || '-'}</div>
                            </div>
                            <div className="col-span-1 md:col-span-2 min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Stop Reason</div>
                                <div className="text-lg break-words">
                                    {getReasonLabel(selectedStop.MachineStopReason)}
                                </div>
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Start Time</div>
                                <div className="text-lg truncate">{formatDate(selectedStop.StartTime)}</div>
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">End Time</div>
                                <div className="text-lg truncate">{formatDate(selectedStop.EndTime)}</div>
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Duration</div>
                                <div className="text-lg truncate">{formatDuration(selectedStop.Duration)}</div>
                            </div>
                            <div className="min-w-0">
                                <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Stop Threshold</div>
                                <div className="text-lg truncate">{selectedStop.StopThreshold}</div>
                            </div>
                            {selectedStop.Comment && selectedStop.Comment !== '-' && (
                                <div className="col-span-1 md:col-span-2 min-w-0">
                                    <div className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Comment</div>
                                    <div className="text-lg break-words">{selectedStop.Comment}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )
                }
            </Modal >
        </div >
    );
};

export default StopCodingList;
